// src/domain/enums.ts
import { z } from "zod";
var flowKindEnum = z.enum(["coreg", "hlp"]);
var campaignStyleEnum = z.enum([
  "co_op",
  "custom_optin",
  "paid_social",
  "cto_email",
  // Epic 096 D1 — the fifth style, the third acquisition journey (an external
  // partner's form POSTs into our /ext interstitial). Sanctioned reopen.
  "external_form"
]);
var campaignSubTypeEnum = z.enum(["regular", "sales_page", "bump", "ty"]);
var campaignStatusEnum = z.enum(["pending", "active", "completed", "scheduled", "test"]);
var limitTypeEnum = z.enum(["total_clicks", "unique_leads", "unique_clicks"]);
var captureStrategyEnum = z.enum(["email_form", "parameter_capture"]);
var consentBasisEnum = z.enum(["on_page", "upstream"]);
var espProviderEnum = z.enum(["maropost", "iterable", "beehiiv"]);
var brandListEnum = z.enum([
  "tt",
  "twm",
  "sis",
  "dmb",
  "superinvestors",
  "thesmartinvestor",
  "fsa"
]);
var eligibilityStepKindEnum = z.enum([
  "cloudflare",
  "email_oversight",
  "internal_duplicate"
]);
var advertiserStatusEnum = z.enum(["active", "inactive"]);
var publisherStatusEnum = z.enum(["active", "inactive"]);
var hashAlgorithmEnum = z.enum(["md5"]);
var consentBasisGrantEnum = z.enum(["on_page", "upstream"]);
var journeyExecutionStatusEnum = z.enum(["running", "completed", "failed"]);
var captureStrategyEventEnum = z.enum(["email_form", "parameter_capture"]);
var promoTypeStatusEnum = z.enum(["active", "inactive"]);
var campaignLinkStatusEnum = z.enum(["active", "inactive"]);
var beehiivLookupStatusEnum = z.enum(["success", "duplicate"]);
var allowListKindEnum = z.enum(["email", "domain"]);
var partnerCredentialKindEnum = z.enum(["bearer", "header", "query", "basic"]);
var attemptKindEnum = z.enum(["pre_ping", "send"]);
var attemptOutcomeEnum = z.enum([
  "accepted",
  "duplicate",
  "rejected",
  "suppressed",
  "error",
  "timeout",
  "skipped"
]);
var espActionKindEnum = z.enum(["subscribe", "journey_trigger", "transactional_send"]);
var espActionOutcomeEnum = z.enum([
  "subscribed",
  "already_subscribed",
  "suppressed",
  "rejected",
  "temporary_failure",
  "permanent_failure",
  "sent",
  "skipped"
]);
var testRunModeEnum = z.enum(["admin", "cto_link", "page"]);
var testRunStatusEnum = z.enum(["queued", "running", "done", "failed"]);
var campaignStatusReasonEnum = z.enum(["cap_total", "manual", "schedule", "end_date"]);
var transactionalOnEnum = z.enum(["success", "valid"]);
var processingStageStatusEnum = z.enum(["pending", "done", "failed", "skipped"]);
var offerTemplateEnum = z.enum(["default", "partner-hero-v1"]);
var crmSendModeEnum = z.enum(["lead_post", "click_out"]);
var crmMethodEnum = z.enum(["GET", "POST"]);
var placeholderSyntaxEnum = z.enum(["percent", "bracket"]);
var partnerEndpointStatusEnum = z.enum(["active", "retired"]);

// src/domain/advertiser/advertiser.schema.ts
import { z as z2 } from "zod";
var advertiserSchema = z2.object({
  id: z2.bigint(),
  name: z2.string().min(1),
  status: advertiserStatusEnum,
  createdAt: z2.date(),
  updatedAt: z2.date()
});

// src/domain/advertiser/create.request.schema.ts
var createAdvertiserSchema = advertiserSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/advertiser/advertiser.response.schema.ts
import { z as z3 } from "zod";
var advertiserResponseSchema = advertiserSchema.extend({
  id: z3.string(),
  createdAt: z3.string(),
  updatedAt: z3.string()
});

// src/domain/publisher/publisher.schema.ts
import { z as z4 } from "zod";
var publisherSchema = z4.object({
  id: z4.bigint(),
  name: z4.string().min(1),
  email: z4.string().email().optional(),
  status: publisherStatusEnum,
  payoutConfig: z4.record(z4.any()).optional(),
  // Reserved, not populated in R1
  createdAt: z4.date(),
  updatedAt: z4.date()
});

// src/domain/publisher/create.request.schema.ts
var createPublisherSchema = publisherSchema.omit({ payoutConfig: true }).partial({ id: true, createdAt: true, updatedAt: true });

// src/domain/publisher/publisher.response.schema.ts
import { z as z5 } from "zod";
var publisherResponseSchema = publisherSchema.extend({
  id: z5.string(),
  createdAt: z5.string(),
  updatedAt: z5.string()
});

// src/domain/site/site.schema.ts
import { z as z6 } from "zod";

// src/domain/site/source-slug.ts
var SOURCE_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
var SOURCE_SLUG_MAX_LENGTH = 64;
var SOURCE_SLUG_FALLBACK = "site";
function buildSourceSlug(label) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]/g, "");
  return slug === "" ? SOURCE_SLUG_FALLBACK : slug;
}
function withSourceSlugSuffix(slug, siteId) {
  return `${slug}-${siteId}`;
}

// src/domain/site/site.schema.ts
var siteSchema = z6.object({
  id: z6.bigint(),
  siteId: z6.string().min(1),
  label: z6.string().min(1),
  sourceSlug: z6.string().regex(SOURCE_SLUG_PATTERN, "Must be lowercase letters, digits and dashes").max(SOURCE_SLUG_MAX_LENGTH).optional(),
  // Buying-terms block (epic 055 README decision 12): nullable — unset terms
  // are empty cells, not zeros.
  cpl: z6.number().nullable(),
  monthlyBudget: z6.number().nullable(),
  leadLimit: z6.number().int().nullable(),
  createdAt: z6.date()
});

// src/domain/site/create.request.schema.ts
import { z as z7 } from "zod";
var createSiteSchema = siteSchema.partial({
  id: true,
  createdAt: true,
  sourceSlug: true
}).extend({
  // Buying-terms block (epic 055 README decision 12): optional on the
  // write surface — omitted means unset, not zero.
  cpl: z7.number().min(0).max(1e6).optional(),
  monthlyBudget: z7.number().min(0).max(1e7).optional(),
  leadLimit: z7.number().int().min(0).optional(),
  // Full-replace INVOICES date list (README decision 12): the service
  // diffs the given set against the site's `site_invoice` rows.
  invoiceDates: z7.array(z7.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(50).optional()
});

// src/domain/site/site.response.schema.ts
import { z as z8 } from "zod";
var siteResponseSchema = siteSchema.extend({
  id: z8.string(),
  createdAt: z8.string(),
  // Buying-terms block + INVOICES dates (epic 055 README decision 12):
  // nullable outputs — unset terms/no invoices are `null`, not `0`/`[]`.
  // Also `.optional()` so existing `SiteResponse`-typed fixtures elsewhere
  // (e.g. 054's campaign-form tests, out of this task's Owns) don't need to
  // grow these four keys — the api always sends them regardless.
  cpl: z8.number().nullable().optional(),
  monthlyBudget: z8.number().nullable().optional(),
  leadLimit: z8.number().int().nullable().optional(),
  invoiceDates: z8.array(z8.string()).nullable().optional()
});

// src/domain/campaign/campaign.schema.ts
import { z as z11 } from "zod";

// src/schema-helpers.ts
import { z as z9 } from "zod";
var BIGINT_INVALID = "__leads_zod_bigint_invalid__";
var toBigIntOrInvalid = (v) => {
  if (typeof v === "bigint") return v;
  if (typeof v === "string" && v.trim().length === 0) return BIGINT_INVALID;
  try {
    return BigInt(v);
  } catch {
    return BIGINT_INVALID;
  }
};
var zCoerceBigInt = () => z9.union([z9.string(), z9.number(), z9.bigint()]).transform(toBigIntOrInvalid).pipe(z9.bigint());
var zCoerceBigIntOptional = () => z9.union([z9.literal(""), z9.null(), z9.string(), z9.number(), z9.bigint()]).transform((v) => {
  if (v === "" || v === null) return void 0;
  return toBigIntOrInvalid(v);
}).pipe(z9.bigint().optional());
var zCoerceDateOptional = () => z9.union([z9.literal(""), z9.null(), z9.coerce.date()]).transform((v) => v === "" || v === null ? void 0 : v).optional();

// src/domain/campaign/crm-config.schema.ts
import { z as z10 } from "zod";
var crmResponseRulesSchema = z10.object({
  duplicate_contains: z10.array(z10.string()).optional(),
  rejected_contains: z10.array(z10.string()).optional(),
  accepted_contains: z10.array(z10.string()).optional()
});
var crmPrepingRuleSchema = z10.object({
  accept_status: z10.array(z10.number().int()).optional(),
  reject_contains: z10.array(z10.string()).optional()
});
var crmAuthSchema = z10.object({
  credential_id: z10.union([z10.number(), z10.string()]).optional(),
  header: z10.string().optional(),
  param: z10.string().optional(),
  kind: z10.string().optional()
});
var crmConfigSchema = z10.object({
  send_mode: crmSendModeEnum.optional(),
  method: crmMethodEnum.optional(),
  get_url: z10.string().optional(),
  post_url: z10.string().nullable().optional(),
  direct_url: z10.string().optional(),
  adapter: z10.string().optional(),
  placeholders: z10.array(z10.string()).optional(),
  placeholder_syntax: placeholderSyntaxEnum.optional(),
  headers: z10.record(z10.string()).optional(),
  auth: crmAuthSchema.optional(),
  body_template: z10.string().nullable().optional(),
  test_values: z10.record(z10.string()).optional(),
  response_rules: crmResponseRulesSchema.optional(),
  preping_rule: crmPrepingRuleSchema.optional(),
  // Epic 047 (ADR-016 §2): the endpoint the campaign points at and the
  // variable values it supplies; absent = inline Simple URL mode. The
  // endpoint's own transport lives on the partner_endpoint row
  // (endpointTransportSchema in the partner-endpoint folder).
  endpoint_id: z10.union([z10.number(), z10.string()]).optional(),
  values: z10.record(z10.string()).optional(),
  // Epic 047 (ADR-016 §5): the structured body form — the same
  // { name, value } shape as the endpoint transport's body_fields; declared
  // inline so the campaign folder and the partner-endpoint folder never
  // import each other.
  body_fields: z10.array(z10.object({ name: z10.string(), value: z10.string() })).optional(),
  // Epic 057 (FR-B35 / ADR-010 A43): campaign-level Skip Duplicates — absent
  // = off. Read by exactly three engine places through
  // `skipDuplicatesOnSql` / `ResolvedCrmConfig.skipDuplicates` (057 README
  // decisions 8–9); it takes NO alias, ever (README decision 8).
  skip_duplicates: z10.boolean().optional(),
  // Epic 057 (FR-B34 / README decision 4): the newsletter value Lifewater
  // sends on its postback — the ops key the postback match rule keys on
  // (entered at campaign recreation, 036 ops config). The matching SQL reads
  // this exact key; a camelCase spelling is inert.
  postback_newsletter: z10.string().optional(),
  // As-built form keys (campaign-form.tsx:605–616) — aliases, all optional
  // strings. Copy-to-canonical happens only in normalizeCrmConfig(); readers
  // never consume an alias directly (A1).
  sendMethod: z10.string().optional(),
  sendSyntax: z10.string().optional(),
  getCrmUrl: z10.string().optional(),
  directCrmUrl: z10.string().optional(),
  postCrmUrl: z10.string().optional()
}).passthrough();
var CRM_CONFIG_ALIASES = {
  getCrmUrl: "get_url",
  postCrmUrl: "post_url",
  directCrmUrl: "direct_url",
  sendMethod: "method",
  sendSyntax: "placeholder_syntax"
};
function normalizeCrmConfig(raw) {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return {};
  }
  const config = { ...crmConfigSchema.parse(raw) };
  if (config.get_url === void 0 && config.getCrmUrl !== void 0) {
    config.get_url = config.getCrmUrl;
  }
  if (config.post_url === void 0 && config.postCrmUrl !== void 0) {
    config.post_url = config.postCrmUrl;
  }
  if (config.direct_url === void 0 && config.directCrmUrl !== void 0) {
    config.direct_url = config.directCrmUrl;
  }
  if (config.method === void 0) {
    const method = config.sendMethod?.toUpperCase();
    if (method === "GET" || method === "POST") {
      config.method = method;
    }
  }
  if (config.placeholder_syntax === void 0) {
    const syntax = config.sendSyntax;
    if (syntax === "percent" || syntax === "bracket") {
      config.placeholder_syntax = syntax;
    }
  }
  if (config.adapter === "get_placeholder") {
    config.adapter = "get_direct";
  }
  return config;
}

// src/domain/campaign/campaign.schema.ts
var advertiserIdSchema = zCoerceBigInt().refine((v) => v !== 0n, {
  message: "Advertiser is required"
});
var campaignSchema = z11.object({
  id: z11.bigint(),
  flowKind: flowKindEnum,
  campaignStyle: campaignStyleEnum,
  advertiserId: advertiserIdSchema,
  publisherId: zCoerceBigInt(),
  clientName: z11.string().optional(),
  name: z11.string().min(1, "Campaign name is required"),
  subType: campaignSubTypeEnum,
  status: campaignStatusEnum,
  scheduleDate: zCoerceDateOptional(),
  startDate: zCoerceDateOptional(),
  endDate: zCoerceDateOptional(),
  priority: z11.number().int().optional(),
  usOnly: z11.boolean(),
  pricePerLead: z11.number().optional(),
  limitType: limitTypeEnum.optional(),
  limitValue: z11.number().int().optional(),
  dailyCap: z11.number().int().optional(),
  // Max Facebook share of daily cap (epic 091 D3): whole percent 0–100; null =
  // no limit (clears on update). Nullable on create AND update: the admin form
  // always sends it for Co-op. Meaning + arithmetic: ./facebook-share.ts.
  maxFacebookSharePct: z11.number().int().min(0).max(100).nullable().optional(),
  // `/ext` interstitial settings (epic 096 D2/D5a): per-campaign error ladder
  // target + the site business key. `errorUrl` is URL-shaped when set (the
  // CTO_FALLBACK_URL zod pattern, task 03); `siteId` is catalog-validated in
  // the service (D5a — the zod layer has no site catalog). Nullable-optional
  // passthrough otherwise — task 02 landed the columns.
  errorUrl: z11.string().url().nullable().optional(),
  siteId: z11.string().nullable().optional(),
  // The FB campaign whose spend maps to this campaign (epic 109 D1 / ruling 1):
  // matched by record ID, so any style may carry it — NOT gated on
  // `external_form`. Digits only (FB ids are long — no min/max); null/absent
  // = no mapping. The service trims and stores '' as NULL; no catalog or
  // uniqueness check here — a wrong id surfaces at cron time (tasks 02/03).
  fbCampaignId: z11.string().regex(/^\d+$/, "Facebook campaign ID must be digits only").nullable().optional(),
  domainRestrictions: z11.array(z11.string()).optional(),
  headline: z11.string().optional(),
  offerBody: z11.string().optional(),
  bannerImageUrl: z11.string().optional(),
  pageSlug: z11.string().optional(),
  pageTitle: z11.string().optional(),
  htmlContent: z11.string().optional(),
  thankYouMessage: z11.string().optional(),
  // Opt-in disclaimer HTML (FR-A37 / 026-02) rendered under the welcome page's
  // email input. Nullable text column; the admin always sends the field and a
  // trimmed-empty value means "clear" (the service stores NULL, which renders
  // the built-in fallback OPT_IN_DISCLAIMER_DEFAULT).
  optInDisclaimer: z11.string().optional(),
  // Offer CTA label (epic 042): the Accept button text on the new offer
  // layout. Nullable text column; same trim-empty = clear contract as the
  // disclaimer (the service stores NULL, which renders the platform default
  // label "Get My Free Report"). A plain label, NOT HTML — never
  // URL-normalized.
  ctaText: z11.string().optional(),
  // Offer-page template discriminator (epic 106 R1/D2): enum-validated here so
  // an unknown id is rejected at the admin boundary (400). Nullable — explicit
  // null clears to the default look; absent = untouched on update, NULL column
  // on create. 'default' stores as-is (NULL and 'default' mean the same look).
  template: offerTemplateEnum.nullable().optional(),
  // Intro Copy (epic 106 D4, amended 2026-10-20): the hero's BlockNote HTML,
  // OUTSIDE htmlContent so the hero/divider split is unambiguous. Same
  // trim-empty = clear contract as ctaText (the service stores NULL = no
  // intro block).
  introHtml: z11.string().optional(),
  // Per-campaign GA click label (FR-A32 / 019-04): stored string rides the event
  // CATEGORY slot; fired on campaign CTA clicks. Nullable varchar(255).
  gaEventLabel: z11.string().max(255).optional(),
  // Opt-in "use as template" flag (019-02 item 15); nullable column, DB default false
  useAsTemplate: z11.boolean().optional(),
  captureNamePhone: z11.boolean(),
  // Typed as of epic 029 (ADR-010 §3 / A1): canonical §2.3.1 keys + the
  // as-built form keys as aliases, all optional, unknown keys kept. Every
  // Stage-C reader goes through normalizeCrmConfig() — nothing reads an alias
  // directly. 034 moves the form to canonical keys.
  crmConfig: crmConfigSchema,
  // Why the campaign left `active` (ADR-010 §3) — worker-owned: the write
  // schemas omit it (005 writes cap_total/end_date; 034 writes manual/schedule).
  statusReason: campaignStatusReasonEnum.optional(),
  // Meta CAPI switch (ADR-010 §10 / A14): null/absent = style default —
  // resolveCapiEnabled() is the single resolver.
  capiEnabled: z11.boolean().nullable().optional(),
  // nullable per ADR-010 A14 (epic 034 decision 12)
  // PATH-2-LITE primitives (nullable for delivery-surface campaigns)
  captureStrategy: captureStrategyEnum.optional(),
  consentBasis: consentBasisEnum.optional(),
  consentConfig: z11.record(z11.any()).optional(),
  // Stub for R1
  createdAt: z11.date(),
  updatedAt: z11.date()
});

// src/domain/campaign/create.request.schema.ts
import { z as z23 } from "zod";

// src/domain/campaign-suppression/campaign-suppression.schema.ts
import { z as z12 } from "zod";
var campaignSuppressionSchema = z12.object({
  id: z12.bigint(),
  campaignId: z12.bigint(),
  enabled: z12.boolean(),
  suppressionFileId: z12.bigint().optional(),
  prepingApiUrl: z12.string().optional(),
  hashAlgorithm: hashAlgorithmEnum,
  createdAt: z12.date(),
  updatedAt: z12.date()
});

// src/domain/campaign-suppression/create.request.schema.ts
var createCampaignSuppressionSchema = campaignSuppressionSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/campaign-cm-config/campaign-cm-config.schema.ts
import { z as z13 } from "zod";
var campaignCmConfigSchema = z13.object({
  id: z13.bigint(),
  campaignId: z13.bigint(),
  enabled: z13.boolean(),
  domain: z13.string().optional(),
  name: z13.string().max(32).optional(),
  // B-05 fix 2026-07-26: ≤32 (Screenshot_9 + master doc l.836)
  slug: z13.string().max(20).optional(),
  // B-05 fix 2026-07-26: ≤20 (Screenshot_9 + master doc l.837)
  createdAt: z13.date(),
  updatedAt: z13.date()
});

// src/domain/campaign-cm-config/create.request.schema.ts
var createCampaignCmConfigSchema = campaignCmConfigSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/campaign-placement/campaign-placement.schema.ts
import { z as z14 } from "zod";
var PLACEMENT_SPOT_MAX = 50;
var campaignPlacementSchema = z14.object({
  id: z14.bigint(),
  campaignId: z14.bigint(),
  sequence: z14.number().int().min(1).max(5).optional(),
  // 1-5
  spot: z14.number().int().min(1).max(PLACEMENT_SPOT_MAX).optional(),
  // 1-PLACEMENT_SPOT_MAX; order only — no spot is special (TY anchor dropped by 019-02 D4)
  isThankYouAnchor: z14.boolean(),
  createdAt: z14.date(),
  updatedAt: z14.date()
});

// src/domain/campaign-placement/create.request.schema.ts
var createCampaignPlacementSchema = campaignPlacementSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/esp-destination/esp-destination.schema.ts
import { z as z15 } from "zod";
var espDestinationSchema = z15.object({
  id: z15.bigint(),
  campaignId: z15.bigint(),
  provider: espProviderEnum,
  brandList: brandListEnum,
  listIdentifier: z15.string().optional(),
  fieldMappings: z15.record(z15.any()).optional(),
  // Q85 transactional send (ADR-010 §3 / A12, epic 029): nullable columns,
  // Maropost rows only in R1. The create schema derives via .partial() so it
  // gains both automatically; the api round-trips them from task 11's hunk.
  transactionalCampaignId: z15.string().optional(),
  transactionalOn: transactionalOnEnum.optional(),
  createdAt: z15.date(),
  updatedAt: z15.date()
});

// src/domain/esp-destination/create.request.schema.ts
var createEspDestinationSchema = espDestinationSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/campaign-eligibility-step/campaign-eligibility-step.schema.ts
import { z as z16 } from "zod";
var campaignEligibilityStepSchema = z16.object({
  id: z16.bigint(),
  campaignId: z16.bigint(),
  stepKind: eligibilityStepKindEnum,
  ord: z16.number().int(),
  // Execution order
  config: z16.record(z16.any()).optional(),
  // Per-step pass/fail/error behavior
  createdAt: z16.date(),
  updatedAt: z16.date()
});

// src/domain/campaign-eligibility-step/create.request.schema.ts
var createCampaignEligibilityStepSchema = campaignEligibilityStepSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
});

// src/domain/campaign-link/campaign-link.schema.ts
import { z as z19 } from "zod";

// src/domain/acquisition-event/acquisition-event.schema.ts
import { z as z17 } from "zod";
var provenanceStringSchema = z17.string().trim().max(512);
var CUSTOM_PARAMS_MAX_KEYS = 20;
var CUSTOM_PARAM_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
var CUSTOM_PARAM_MAX_VALUE_LENGTH = 512;
var customParamsSchema = z17.record(
  z17.string().regex(CUSTOM_PARAM_KEY_PATTERN, "Key must match [A-Za-z0-9_-]{1,64}"),
  z17.string().max(CUSTOM_PARAM_MAX_VALUE_LENGTH)
).refine((params) => Object.keys(params).length <= CUSTOM_PARAMS_MAX_KEYS, {
  message: `At most ${CUSTOM_PARAMS_MAX_KEYS} custom params are accepted`
});
var sourceParamsSchema = z17.object({
  siteId: z17.string().optional(),
  subId: z17.string().optional(),
  ipAddress: z17.string().optional(),
  utmSource: provenanceStringSchema.optional(),
  utmMedium: provenanceStringSchema.optional(),
  utmCampaign: provenanceStringSchema.optional(),
  utmContent: provenanceStringSchema.optional(),
  referringSite: provenanceStringSchema.optional(),
  customParams: customParamsSchema.optional(),
  campaignLinkId: z17.string().optional()
});
var acquisitionEventSchema = z17.object({
  id: z17.bigint(),
  lead: z17.bigint(),
  subscriber: z17.bigint(),
  campaign: z17.bigint(),
  captureStrategy: captureStrategyEventEnum,
  sourceParams: sourceParamsSchema.optional(),
  eligibilityOutcomes: z17.record(z17.any()).optional(),
  createdAt: z17.date()
});

// src/domain/acquisition-event/acquisition-event.response.schema.ts
import { z as z18 } from "zod";
var acquisitionEventResponseSchema = z18.object({
  id: z18.string(),
  lead: z18.string(),
  subscriber: z18.string(),
  campaign: z18.string(),
  captureStrategy: captureStrategyEventEnum,
  sourceParams: sourceParamsSchema.optional(),
  eligibilityOutcomes: z18.record(z18.any()).optional(),
  createdAt: z18.string()
});

// src/domain/campaign-link/campaign-link.schema.ts
var campaignLinkValidationsSchema = z19.object({
  cloudflare: z19.boolean(),
  emailOversight: z19.boolean()
});
var campaignLinkCustomParamSchema = z19.object({
  key: z19.string().regex(CUSTOM_PARAM_KEY_PATTERN, "Key must match [A-Za-z0-9_-]{1,64}"),
  value: z19.string().max(CUSTOM_PARAM_MAX_VALUE_LENGTH)
});
var campaignLinkCustomParamsSchema = z19.array(campaignLinkCustomParamSchema).max(CUSTOM_PARAMS_MAX_KEYS);
var campaignLinkSchema = z19.object({
  id: z19.bigint(),
  campaignId: z19.bigint(),
  siteId: z19.bigint(),
  promoTypeId: z19.bigint(),
  price: z19.number().optional(),
  validations: campaignLinkValidationsSchema,
  status: campaignLinkStatusEnum,
  mergeTag: z19.string().min(1),
  customParams: campaignLinkCustomParamsSchema,
  createdAt: z19.date(),
  updatedAt: z19.date()
});

// src/domain/campaign-link/campaign-link.response.schema.ts
import { z as z20 } from "zod";
var campaignLinkResponseSchema = campaignLinkSchema.extend({
  id: z20.string(),
  campaignId: z20.string(),
  siteId: z20.string(),
  promoTypeId: z20.string(),
  price: z20.number().nullable().optional(),
  // numeric(10,2) NULL; mapper coerces null → undefined
  customParams: campaignLinkCustomParamsSchema.optional(),
  createdAt: z20.string(),
  updatedAt: z20.string(),
  url: z20.string().min(1)
});

// src/domain/campaign-link/resolve.response.schema.ts
import { z as z21 } from "zod";
var ctoResolveResponseSchema = z21.discriminatedUnion("found", [
  z21.object({
    found: z21.literal(true),
    /** The resolved link's id (bigint PK as string) — the `utm_content` value. */
    linkId: z21.string(),
    /** The parent campaign's id as string — the capture request's `campaignRef`. */
    campaignRef: z21.string(),
    /** The traffic source's business key (`site.site_id`, e.g. `'130'`). */
    siteId: z21.string(),
    /** The promo type's slug — the link's `utm_campaign` value. */
    promoSlug: z21.string(),
    /** `false` → the click must go to `fallbackUrl` (no capture). */
    active: z21.boolean(),
    /** Per-link validations (ADR-007 #3) — which checks the capture runs. */
    validations: campaignLinkValidationsSchema,
    /**
     * Epic 108 D4: `true` = this click's email domain is off the approved
     * list (and the list is non-empty) — the click must pass the Turnstile
     * challenge before its capture is accepted. Required, not defaulted: one
     * repo, one deploy.
     */
    domainGate: z21.boolean(),
    /** Redirect target for this click when it cannot be captured (ADR-007 #7). */
    fallbackUrl: z21.string().url()
  }),
  z21.object({
    found: z21.literal(false),
    fallbackUrl: z21.string().url()
  })
]);

// src/domain/campaign-link/resolve-query-keys.ts
var ctoResolveQueryKeys = {
  /** The link id — resolution is by id first. */
  utmContent: "utm_content",
  /** The promo slug (fallback resolution). */
  utmCampaign: "utm_campaign",
  /** The site business key (fallback resolution). */
  utmSiteid: "utm_siteid",
  /**
   * The click's restored (plus-addressed) email — the domain-gate input
   * (epic 108 D4).
   */
  leadEmail: "lead_email"
};
var extResolveQueryKeys = {
  /** The `/ext` path's campaignRef segment — digit id or page slug. */
  ref: "ref"
};

// src/domain/campaign-link/create.request.schema.ts
import { z as z22 } from "zod";

// src/domain/campaign-link/merge-tag-presets.ts
var ctoMergeTagPresets = [
  {
    provider: "maropost",
    mergeTag: "{{contact.email}}",
    label: "Maropost \u2014 {{contact.email}}"
  },
  {
    provider: "iterable",
    mergeTag: "{{email}}",
    label: "Iterable \u2014 {{email}}"
  },
  {
    provider: "beehiiv",
    mergeTag: "{{email}}",
    label: "Beehiiv \u2014 {{email}}"
  }
];
var CTO_DEFAULT_MERGE_TAG = "{{contact.email}}";
var CTO_MERGE_TAG_MAX_LENGTH = 128;
function ctoMergeTagForProvider(provider) {
  return ctoMergeTagPresets.find((preset) => preset.provider === provider)?.mergeTag;
}

// src/domain/campaign-link/create.request.schema.ts
var campaignLinkValidationsInputSchema = z22.object({
  cloudflare: z22.boolean().default(true),
  emailOversight: z22.boolean().default(true)
});
var createCampaignLinkSchema = campaignLinkSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  campaignId: zCoerceBigIntOptional().optional(),
  siteId: zCoerceBigInt(),
  promoTypeId: zCoerceBigInt(),
  price: z22.number().min(0).optional(),
  validations: campaignLinkValidationsInputSchema,
  status: campaignLinkStatusEnum.optional(),
  // default 'active' in the DB
  mergeTag: z22.string().min(1).max(CTO_MERGE_TAG_MAX_LENGTH).optional(),
  customParams: campaignLinkCustomParamsSchema.optional()
});

// src/domain/campaign-link/update.request.schema.ts
var updateCampaignLinkSchema = createCampaignLinkSchema.partial();

// src/domain/campaign/create.request.schema.ts
var createCampaignSchema = campaignSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true,
  campaignStyle: true,
  // additive-optional for three-phase deploy
  flowKind: true,
  // derived from campaignStyle when present
  advertiserId: true
  // epic 043: style-conditional requirement lives in the service (README decision 3)
}).omit({
  publisherId: true,
  // Worker-owned (ADR-010 §3): 005 writes cap_total/end_date, 034 writes
  // manual/schedule — the admin never sets it on create/update.
  statusReason: true
});
var campaignSiteRestrictionElementSchema = z23.object({
  id: zCoerceBigIntOptional()
});
var createCampaignSuppressionSchemaLocal = createCampaignSuppressionSchema.extend({
  campaignId: z23.bigint().optional()
}).partial();
var createCampaignCmConfigSchemaLocal = createCampaignCmConfigSchema.extend({
  campaignId: z23.bigint().optional()
}).partial();
var createCampaignPlacementSchemaLocal = createCampaignPlacementSchema.extend({
  campaignId: z23.bigint().optional()
}).partial();
var createEspDestinationSchemaLocal = createEspDestinationSchema.extend({
  campaignId: z23.bigint().optional()
}).partial();
var createCampaignEligibilityStepSchemaLocal = createCampaignEligibilityStepSchema.extend({
  campaignId: z23.bigint().optional()
}).partial();
var campaignAggregateCreateSchema = createCampaignSchema.extend({
  // 1:1 children
  suppression: createCampaignSuppressionSchemaLocal.optional(),
  cmConfig: createCampaignCmConfigSchemaLocal.optional(),
  placement: createCampaignPlacementSchemaLocal.optional(),
  // HLP-only; nullable for coreg
  // 1:N collections
  espDestinations: z23.array(createEspDestinationSchemaLocal),
  eligibilitySteps: z23.array(createCampaignEligibilityStepSchemaLocal),
  siteRestrictions: z23.array(campaignSiteRestrictionElementSchema),
  // CTO link drafts (epic 037-06, operator ruling 2026-09-09 — reverses 024
  // README decision #5). The per-element schema is the single-link create
  // schema reused unchanged: `campaignId` stays optional and is ignored here
  // (the service attaches the parent it just created).
  links: z23.array(createCampaignLinkSchema).optional()
}).partial({
  id: true,
  createdAt: true,
  updatedAt: true,
  usOnly: true,
  captureNamePhone: true,
  status: true,
  subType: true,
  flowKind: true
  // derived from campaignStyle when present
}).strict();
var campaignAggregateUpdateSchema = campaignAggregateCreateSchema.omit({ links: true }).extend({
  placement: createCampaignPlacementSchemaLocal.nullable(),
  espDestinations: z23.array(
    createEspDestinationSchemaLocal.extend({
      transactionalCampaignId: z23.string().nullable().optional(),
      transactionalOn: transactionalOnEnum.nullable().optional()
    })
  ),
  priority: z23.number().int().nullable().optional(),
  dailyCap: z23.number().int().nullable().optional(),
  limitValue: z23.number().int().nullable().optional()
}).partial();

// src/domain/campaign/response.schema.ts
import { z as z24 } from "zod";
var campaignSiteRestrictionResponseSchema = z24.object({
  id: z24.string(),
  // numeric site.id as string (JSON-safe)
  siteId: z24.string()
  // string business key site.site_id ('tt', 'twm', ...)
});
var campaignResponseSchema = campaignSchema.extend({
  id: z24.string(),
  // bigint → string
  advertiserId: z24.string(),
  // bigint → string
  publisherId: z24.string(),
  // bigint → string
  scheduleDate: z24.string().optional(),
  // Date → ISO string (mapper coerces null → undefined)
  startDate: z24.string().optional(),
  // Date → ISO string (mapper coerces null → undefined)
  endDate: z24.string().optional(),
  // Date → ISO string (mapper coerces null → undefined)
  createdAt: z24.string(),
  // Date → ISO string
  updatedAt: z24.string(),
  // Date → ISO string
  campaignStyle: campaignStyleEnum.nullable().optional()
  // not null in DB, but nullable in response for forward compat
}).extend({
  // Override nullable PATH-2-LITE primitives (delivery-surface campaigns have null)
  // Note: Zod .extend() doesn't override TS types correctly, so runtime accepts null
  // but TypeScript doesn't reflect it. Mapper converts null → undefined for TS.
  captureStrategy: captureStrategyEnum.nullable().optional(),
  consentBasis: consentBasisEnum.nullable().optional(),
  // Nullable scalars/arrays: the mapper forwards MikroORM entity values
  // verbatim, and a NULL DB column loads as `null` (not `undefined`). The
  // base schema's `.optional()` accepts `undefined` but rejects `null`, so
  // every nullable column must be overridden `.nullable()` here. Only the
  // Date fields above are exempt — the mapper coerces them with
  // `?.toISOString()`, turning null → undefined before validation.
  clientName: z24.string().nullable().optional(),
  priority: z24.number().int().nullable().optional(),
  pricePerLead: z24.union([z24.string(), z24.number(), z24.null(), z24.undefined()]).nullable().optional(),
  limitType: limitTypeEnum.nullable().optional(),
  limitValue: z24.number().int().nullable().optional(),
  dailyCap: z24.number().int().nullable().optional(),
  domainRestrictions: z24.array(z24.string()).nullable().optional(),
  headline: z24.string().nullable().optional(),
  offerBody: z24.string().nullable().optional(),
  bannerImageUrl: z24.string().nullable().optional(),
  pageSlug: z24.string().nullable().optional(),
  pageTitle: z24.string().nullable().optional(),
  htmlContent: z24.string().nullable().optional(),
  thankYouMessage: z24.string().nullable().optional(),
  optInDisclaimer: z24.string().nullable().optional(),
  // nullable text column (HTML)
  ctaText: z24.string().nullable().optional(),
  // nullable text column (CTA label, epic 042)
  gaEventLabel: z24.string().max(255).nullable().optional(),
  // nullable column
  useAsTemplate: z24.boolean().nullable().optional(),
  // nullable column (default false)
  consentConfig: z24.record(z24.any()).nullable().optional(),
  // Stage-C nullable columns (epic 029 / ADR-010 §3): `crmConfig` needs no
  // override — the typed schema is inherited from campaignSchema and the
  // mapper forwards the row; .passthrough() keeps unknown keys intact.
  statusReason: campaignStatusReasonEnum.nullable().optional(),
  capiEnabled: z24.boolean().nullable().optional(),
  // `/ext` interstitial columns (epic 096 D2/D5a): nullable text columns —
  // the mapper forwards the row's null verbatim.
  errorUrl: z24.string().nullable().optional(),
  siteId: z24.string().nullable().optional(),
  // FB campaign mapping key (epic 109 D1/ruling 1): nullable column — the
  // mapper forwards the row's null verbatim; no digits pattern here (the
  // stored value already passed the write schema).
  fbCampaignId: z24.string().nullable().optional(),
  // Offer-page template + Intro Copy (epic 106 R1/D4, ruling 2026-10-20):
  // nullable columns — the mapper forwards the row's null verbatim.
  template: offerTemplateEnum.nullable().optional(),
  introHtml: z24.string().nullable().optional()
}).extend({
  // 1:1 children (nullable)
  suppression: campaignSuppressionSchema.extend({
    id: z24.string(),
    campaignId: z24.string(),
    suppressionFileId: z24.string().nullable(),
    prepingApiUrl: z24.string().nullable().optional(),
    // nullable column
    createdAt: z24.string(),
    updatedAt: z24.string()
  }).nullable().optional(),
  cmConfig: campaignCmConfigSchema.extend({
    id: z24.string(),
    campaignId: z24.string(),
    domain: z24.string().nullable().optional(),
    // nullable column
    name: z24.string().max(32).nullable().optional(),
    // nullable column
    slug: z24.string().max(20).nullable().optional(),
    // nullable column
    createdAt: z24.string(),
    updatedAt: z24.string()
  }).nullable().optional(),
  placement: campaignPlacementSchema.extend({
    id: z24.string(),
    campaignId: z24.string(),
    sequence: z24.number().int().min(1).max(5).nullable().optional(),
    // nullable column
    spot: z24.number().int().min(1).max(PLACEMENT_SPOT_MAX).nullable().optional(),
    // nullable column
    createdAt: z24.string(),
    updatedAt: z24.string()
  }).nullable().optional(),
  // 1:N collections
  espDestinations: z24.array(
    espDestinationSchema.extend({
      id: z24.string(),
      campaignId: z24.string(),
      listIdentifier: z24.string().nullable().optional(),
      // nullable column
      fieldMappings: z24.record(z24.any()).nullable().optional(),
      // nullable column
      // Q85 transactional send (ADR-010 §3 / A12): Maropost rows only in R1;
      // 034 adds the form controls, until then SQL-set values ride through.
      transactionalCampaignId: z24.string().nullable().optional(),
      // nullable column
      transactionalOn: transactionalOnEnum.nullable().optional(),
      // nullable column
      createdAt: z24.string(),
      updatedAt: z24.string()
    })
  ),
  eligibilitySteps: z24.array(
    campaignEligibilityStepSchema.extend({
      id: z24.string(),
      campaignId: z24.string(),
      createdAt: z24.string(),
      updatedAt: z24.string()
    })
  ),
  siteRestrictions: z24.array(campaignSiteRestrictionResponseSchema)
});
var campaignListResponseSchema = z24.array(campaignResponseSchema);

// src/domain/campaign/public-response.schema.ts
import { z as z25 } from "zod";
var publicCmConfigSchema = campaignResponseSchema.shape.cmConfig.unwrap().unwrap().pick({ enabled: true, domain: true, name: true, slug: true });
var publicEligibilityStepSchema = campaignResponseSchema.shape.eligibilitySteps.element.pick({
  stepKind: true
});
var publicCrmConfigSchema = crmConfigSchema.pick({ send_mode: true, direct_url: true }).strip();
var publicCampaignResponseSchema = campaignResponseSchema.pick({
  id: true,
  flowKind: true,
  campaignStyle: true,
  captureStrategy: true,
  pageSlug: true,
  pageTitle: true,
  headline: true,
  offerBody: true,
  bannerImageUrl: true,
  htmlContent: true,
  thankYouMessage: true,
  optInDisclaimer: true,
  ctaText: true,
  gaEventLabel: true
}).extend({
  cmConfig: publicCmConfigSchema.nullable().optional(),
  eligibilitySteps: z25.array(publicEligibilityStepSchema).optional(),
  crmConfig: publicCrmConfigSchema.optional(),
  // Offer-page template + Intro Copy (epic 106 R1/D4, ruling 2026-10-20):
  // carried non-null and trimmed by the projection — the builder omits each
  // key when the column is null/empty (or the template is 'default') so
  // old-shape payloads parse unchanged. An unknown template id is the
  // client's degradation case, never a parse failure the public feed can
  // produce.
  template: offerTemplateEnum.optional(),
  introHtml: z25.string().optional()
});
var publicCampaignListResponseSchema = z25.array(publicCampaignResponseSchema);

// src/domain/campaign/ext-resolve.response.schema.ts
import { z as z26 } from "zod";
var extResolveResponseSchema = z26.discriminatedUnion("ok", [
  z26.object({
    ok: z26.literal(true),
    /** The resolved campaign's id as string — the capture request's `campaignRef`. */
    campaignRef: z26.string(),
    /** Always `true` on this branch (inactive campaigns answer `ok: false`). */
    active: z26.boolean(),
    /** Where a capture that cannot complete sends the visitor (the error ladder). */
    errorUrl: z26.string().url(),
    /** The campaign's `site_id` business key (`site.site_id`, e.g. `'fsa'`), null when unset. */
    siteId: z26.string().nullable(),
    /**
     * The campaign's `thank_you_message` — the header message the FSA visitor
     * sees as the confirmation bar on every offer step (D7a). Null when unset;
     * the interstitial then skips the sessionStorage bar write.
     */
    thankYouMessage: z26.string().nullable(),
    /** Whether the campaign has a `cloudflare` eligibility step (B1) — the interstitial waits for the Turnstile token. */
    requiresTurnstile: z26.boolean()
  }),
  z26.object({
    ok: z26.literal(false),
    /** Why no capture may run: wrong style, or the campaign is not active. */
    reason: z26.enum(["not_external_form", "inactive"]),
    /** The same resolved ladder target as the ok branch. */
    errorUrl: z26.string().url()
  })
]);

// src/domain/campaign/schedule.request.schema.ts
import { z as z27 } from "zod";
var campaignScheduleRequestSchema = z27.object({
  scheduleDate: z27.string().datetime({ message: "Must be a valid ISO 8601 datetime string" })
});

// src/domain/campaign/run-window.ts
var toMs = (v) => {
  if (v === null || v === void 0) {
    return null;
  }
  const ms = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isNaN(ms) ? null : ms;
};
function isWithinRunWindow(startDate, endDate, now) {
  const nowMs = toMs(now);
  if (nowMs === null) {
    throw new Error("isWithinRunWindow: now must be a valid instant");
  }
  const startMs = toMs(startDate);
  if (startMs !== null && nowMs < startMs) {
    return false;
  }
  const endMs = toMs(endDate);
  if (endMs !== null && nowMs > endMs) {
    return false;
  }
  return true;
}

// src/domain/campaign/opt-in-disclaimer.ts
var OPT_IN_DISCLAIMER_DEFAULT = 'By opting in you agree to receive email updates and special offers from TradingTips.com and its affiliates, <a href="https://www.tradingtips.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy policy</a>. As a bonus, you will also receive a free subscription to the Chaikin Analytics Powerfeed Newsletter, <a href="https://www.chaikinanalytics.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy policy</a>. You may opt-out at any time.';

// src/domain/campaign/capi.ts
var CAPI_DEFAULT_ON_STYLES = [
  "paid_social",
  "cto_email",
  "external_form"
];
function resolveCapiEnabled(campaign) {
  return campaign.capiEnabled ?? CAPI_DEFAULT_ON_STYLES.includes(campaign.campaignStyle);
}

// src/domain/campaign/facebook-share.ts
var FACEBOOK_SITE_ID = "101";
function facebookShareLimit(sharePct, dailyCap) {
  if (sharePct == null) return null;
  if (sharePct === 0) return 0;
  if (dailyCap == null) return null;
  return Math.floor(sharePct * dailyCap / 100);
}

// src/domain/campaign-asset/response.schema.ts
import { z as z28 } from "zod";
var campaignAssetUploadResponseSchema = z28.object({
  url: z28.string().url(),
  originalFilename: z28.string(),
  sha256: z28.string().regex(/^[a-f0-9]{64}$/, "sha256 must be 64 lowercase hex characters"),
  byteSize: z28.number().int().nonnegative()
});

// src/domain/campaign-site-restriction/campaign-site-restriction.schema.ts
import { z as z29 } from "zod";
var campaignSiteRestrictionSchema = z29.object({
  campaignId: z29.bigint(),
  siteId: z29.bigint()
});

// src/domain/suppression-file/suppression-file.schema.ts
import { z as z30 } from "zod";
var suppressionFileSchema = z30.object({
  id: z30.bigint(),
  advertiserId: z30.bigint(),
  originalFilename: z30.string().min(1),
  storagePath: z30.string().min(1),
  rowCount: z30.number().int().optional(),
  hashAlgorithm: hashAlgorithmEnum,
  uploadedBy: z30.string().min(1),
  createdAt: z30.date()
});

// src/domain/suppression-file/create.request.schema.ts
var createSuppressionFileSchema = suppressionFileSchema.partial({
  id: true,
  createdAt: true
});

// src/domain/suppression-file/response.schema.ts
import { z as z31 } from "zod";
var suppressionFileResponseSchema = suppressionFileSchema.extend({
  id: z31.string(),
  advertiserId: z31.string(),
  createdAt: z31.string()
});

// src/domain/suppression-file/list.response.schema.ts
import { z as z32 } from "zod";
var suppressionFileAttachedCampaignSchema = z32.object({
  id: z32.string(),
  name: z32.string(),
  campaignStyle: campaignStyleEnum
});
var suppressionFileListRowResponseSchema = suppressionFileResponseSchema.extend({
  advertiserName: z32.string(),
  attachedCampaigns: z32.array(suppressionFileAttachedCampaignSchema)
});

// src/domain/lead/lead.schema.ts
import { z as z34 } from "zod";

// src/domain/lead/processing-state.schema.ts
import { z as z33 } from "zod";
var stageStateSchema = z33.object({
  status: processingStageStatusEnum,
  updatedAt: z33.string(),
  attempts: z33.number().int().optional(),
  detail: z33.record(z33.any()).optional()
});
var processingStateSchema = z33.object({
  distribution: stageStateSchema.optional(),
  esp: stageStateSchema.optional(),
  capi: stageStateSchema.optional()
});

// src/domain/lead/lead.schema.ts
var leadSchema = z34.object({
  id: z34.bigint(),
  campaign: z34.bigint(),
  subscriber: z34.bigint(),
  campaignLinkId: z34.bigint().optional(),
  emailOriginal: z34.string(),
  emailHash: z34.string(),
  siteId: z34.string().optional(),
  subId: z34.string().optional(),
  ipAddress: z34.string().optional(),
  processingFlags: z34.string().optional(),
  // Stage-C worker progress (ADR-010 §3 / A5) — nullable jsonb column; each
  // handler writes only its own stage key. `processingFlags` above stays,
  // deprecated, untouched.
  processingState: processingStateSchema.optional(),
  crmResponse: z34.record(z34.any()).optional(),
  isTest: z34.boolean(),
  beehiivLookupStatus: beehiivLookupStatusEnum.optional(),
  statusDetail: z34.record(z34.any()).optional(),
  country: z34.string().optional(),
  createdAt: z34.date()
});

// src/domain/lead/lead.response.schema.ts
import { z as z36 } from "zod";

// src/api/envelope.ts
import { z as z35 } from "zod";
var apiResponseSchema = (data) => z35.object({ data });

// src/domain/lead/lead.response.schema.ts
var leadResponseSchema = z36.object({
  id: z36.string(),
  campaign: z36.string(),
  subscriber: z36.string(),
  campaignLinkId: z36.string().optional(),
  emailOriginal: z36.string(),
  emailHash: z36.string(),
  siteId: z36.string().optional(),
  subId: z36.string().optional(),
  ipAddress: z36.string().optional(),
  processingFlags: z36.string().optional(),
  // Stage-C worker progress (ADR-010 §3) — nullable column, so the mapper may
  // forward null (no stage written yet).
  processingState: processingStateSchema.nullable().optional(),
  crmResponse: z36.record(z36.any()).optional(),
  isTest: z36.boolean(),
  // Null in the DB means "not checked" — the mapper omits it (task 06), so the
  // wire field is optional rather than `nullable` like the other lists here.
  beehiivLookupStatus: beehiivLookupStatusEnum.optional(),
  statusDetail: z36.record(z36.any()).optional(),
  country: z36.string().optional(),
  createdAt: z36.string()
});
var leadApiResponseSchema = apiResponseSchema(leadResponseSchema);

// src/domain/lead/capture.request.schema.ts
import { z as z37 } from "zod";
var captureRequestSchema = z37.object({
  email: z37.string().min(1).email("Invalid email address"),
  campaignRef: z37.string().min(1, "Campaign reference is required"),
  siteId: z37.string().optional(),
  campaignLinkId: z37.string().regex(/^\d+$/, "campaignLinkId must be a string of digits").optional(),
  subId: z37.string().optional(),
  turnstileToken: z37.string().optional(),
  ipAddress: z37.string().optional(),
  utmSource: provenanceStringSchema.optional(),
  utmMedium: provenanceStringSchema.optional(),
  utmCampaign: provenanceStringSchema.optional(),
  utmContent: provenanceStringSchema.optional(),
  referringSite: provenanceStringSchema.optional(),
  customParams: customParamsSchema.optional()
});

// src/domain/lead/capture.response.schema.ts
import { z as z39 } from "zod";

// src/domain/offers/walk-step.schema.ts
import { z as z38 } from "zod";
var offerStepSchema = z38.object({
  type: z38.literal("offer"),
  rawHtml: z38.string(),
  placementId: z38.string(),
  sequence: z38.number().int().min(1).max(5),
  spot: z38.number().int().min(1).max(PLACEMENT_SPOT_MAX),
  // Campaign's stored GA click label (FR-A32 / 019-04) — additive-optional per
  // the ADR-005 hard requirement ("additive-optional schema fields only").
  // Absent when the step's campaign has no label → client fires nothing.
  gaEventLabel: z38.string().max(255).optional(),
  // ── Epic 042: structured offer-layout fields (additive-optional, same
  // ADR-005 rule). The landing chrome places these directly; when a field is
  // absent OR empty the client falls back — pageHtml → rawHtml, cta → the
  // default label, disclaimer → the standard fallback line built from
  // advertiserName. `rawHtml` stays REQUIRED and unchanged (the pre-042
  // compat path for both old clients and empty campaigns).
  // pageHtml = campaign.html_content (URL-decoded at the serve boundary).
  pageHtml: z38.string().optional(),
  // cta = campaign.cta_text (the Accept label, verbatim — a label, not HTML).
  cta: z38.string().optional(),
  // disclaimer = campaign.opt_in_disclaimer (URL-decoded at the serve boundary).
  disclaimer: z38.string().optional(),
  // advertiserName = derived at read time from campaign.advertiser.name.
  advertiserName: z38.string().optional(),
  // Resolved display title for the browser tab (task 037-07): the walk engine
  // resolves pageTitle || headline || pageSlug server-side; this contract only
  // carries the result — additive-optional per the ADR-005 hard requirement
  // (019-04 gaEventLabel precedent). Absent when the chain comes up empty →
  // the client leaves the current title untouched.
  pageTitle: z38.string().optional(),
  // Accept-time open target (QA #64, task 038-01): resolved server-side by
  // the SAME send-mode rule the accept POST applies (`accept-skip.service.ts`
  // `resolveTargetUrl` is the semantic source — click_out → direct_url,
  // lead_post → direct_url or nothing, unknown → nothing). Absent = the
  // client opens NO tab; the accept POST alone advances the walk. When
  // present it only gates the client's pre-opened window — the accept POST
  // RESPONSE's `targetUrl` stays the navigate-vs-close authority.
  // Additive-optional per the ADR-005 hard requirement (019-04 precedent):
  // old-shape payloads parse unchanged.
  targetUrl: z38.string().url().optional(),
  // ── Epic 082: the offer-page redesign's two additive-optional fields (same
  // ADR-005 rule — old-shape payloads parse unchanged).
  // partnerName = the populated advertiser's name (campaign.advertiser.name),
  // on EVERY send mode — it feeds the eyebrow line "PARTNER OFFER FROM …".
  // Unlike `advertiserName` (HF-082: lead_post only, it switches the
  // automatic disclaimer) it carries no behaviour. Absent when the campaign
  // has no advertiser → the client shows the bare "PARTNER OFFER".
  partnerName: z38.string().optional(),
  // imageUrl = campaign.banner_image_url, trimmed, only when it parses as an
  // absolute http(s) URL (the `targetUrl` rule, so a malformed stored value
  // cannot fail the client's `z.string().url()` step parse). Absent = the page
  // renders as a single column with no image.
  imageUrl: z38.string().url().optional(),
  // ── Epic 082 task 04: headline = campaign.headline (headline), trimmed.
  // The walk renders `pageHtml` (never `rawHtml`, which is where the stored
  // headline is injected) for every page with content, so a paragraph-first
  // page would show no headline at all; the layout shows this text as the
  // page headline only when the content brings no leading heading of its own.
  // Plain text, never HTML. Absent when the stored headline is empty → no
  // fallback headline.
  headline: z38.string().optional(),
  // ── Epic 106 R1: the offer-page template discriminator — additive-optional
  // (same ADR-005 rule). Absent ⇒ 'default'; an unknown id is the CLIENT's
  // degradation case (R3: the landing-client registry falls back to the
  // default look), so the schema accepts only the known enum — the API never
  // invents an id. The walk step needs no intro: the Intro Copy / Main Body
  // split is a custom-page concept (D4), so `introHtml` never rides a step.
  template: offerTemplateEnum.optional()
});
var thankYouStepSchema = z38.object({
  type: z38.literal("thank-you"),
  rawHtml: z38.string(),
  // Constant 'Thank you' (task 037-07 operator ruling: a generic message, NOT
  // any campaign's title — no chain runs here) — additive-optional per the
  // ADR-005 hard requirement (019-04 gaEventLabel precedent). Absent stays
  // valid, so old-shape payloads parse unchanged.
  pageTitle: z38.string().optional()
});
var walkStepResponseSchema = z38.discriminatedUnion("type", [
  offerStepSchema,
  thankYouStepSchema
]);

// src/domain/lead/capture.response.schema.ts
var failedStepSchema = z39.object({
  stepKind: eligibilityStepKindEnum,
  outcome: z39.enum(["fail", "error"]),
  reason: z39.string()
});
var captureResponseSchema = z39.object({
  leadId: z39.string(),
  status: z39.enum(["accepted", "rejected"]),
  reason: z39.string().optional(),
  redirectUrl: z39.string().optional(),
  walk: walkStepResponseSchema.nullable().optional(),
  failedSteps: z39.array(failedStepSchema).optional()
});

// src/domain/lead/lead-list.response.schema.ts
import { z as z40 } from "zod";
var leadListRowResponseSchema = z40.object({
  id: z40.string(),
  emailOriginal: z40.string(),
  campaignId: z40.string(),
  campaignName: z40.string().nullable(),
  isTest: z40.boolean(),
  journeyStatus: journeyExecutionStatusEnum.optional(),
  campaignLinkId: z40.string().optional(),
  beehiivLookupStatus: beehiivLookupStatusEnum.optional(),
  statusDetail: z40.record(z40.any()).optional(),
  country: z40.string().optional(),
  // Task 097-01 (her 2026-10-13 ruling): the capture IP as stored — the
  // Leads list column and the search box's exact-IP filter. Absent when NULL.
  ipAddress: z40.string().optional(),
  createdAt: z40.string()
});
var leadListResponseSchema = z40.object({
  rows: leadListRowResponseSchema.array(),
  total: z40.number()
});
var leadListApiResponseSchema = apiResponseSchema(leadListResponseSchema);

// src/domain/lead/lead-detail.response.schema.ts
import { z as z41 } from "zod";
var leadShareResponseSchema = z41.object({
  id: z41.string(),
  advertiserName: z41.string(),
  campaignId: z41.string(),
  campaignName: z41.string(),
  outcome: attemptOutcomeEnum,
  reason: z41.string().nullable(),
  sentAt: z41.string()
});
var leadPostbackResponseSchema = z41.object({
  id: z41.string(),
  receivedAt: z41.string(),
  status: z41.string().nullable(),
  newsletter: z41.string().nullable(),
  matchedBy: z41.enum(["newsletter", "email"]).nullable(),
  campaignId: z41.string().nullable()
});
var leadDetailResponseSchema = z41.object({
  id: z41.string(),
  emailOriginal: z41.string(),
  campaignId: z41.string(),
  campaignName: z41.string().nullable(),
  isTest: z41.boolean(),
  journeyStatus: journeyExecutionStatusEnum.optional(),
  beehiivLookupStatus: beehiivLookupStatusEnum.optional(),
  statusDetail: z41.record(z41.any()).optional(),
  country: z41.string().optional(),
  // As stored, nullable here — the list row (097-01) carries it as an
  // optional field; the detail always carries the key.
  ipAddress: z41.string().nullable(),
  // Detail-only fields (043-03 Scope 2) — never on the list row.
  siteId: z41.string().optional(),
  subId: z41.string().optional(),
  pageSlug: z41.string().optional(),
  campaignLinkId: z41.string().optional(),
  linkSiteId: z41.string().optional(),
  linkSiteLabel: z41.string().optional(),
  linkPromoSlug: z41.string().optional(),
  createdAt: z41.string(),
  shares: leadShareResponseSchema.array(),
  // Epic 057 decision 6: the lead's inbound advertiser postbacks (the same
  // "always present, empty array" rule as `shares` — the UI renders its empty
  // state, never hides the section).
  postbacks: leadPostbackResponseSchema.array()
});
var leadDetailApiResponseSchema = apiResponseSchema(leadDetailResponseSchema);

// src/domain/subscriber/subscriber.schema.ts
import { z as z42 } from "zod";
var subscriberSchema = z42.object({
  id: z42.bigint(),
  emailNormalized: z42.string(),
  firstSeenAt: z42.date(),
  lastSeenAt: z42.date(),
  attributes: z42.record(z42.any()).optional()
});

// src/domain/subscriber/subscriber.response.schema.ts
import { z as z43 } from "zod";
var subscriberResponseSchema = z43.object({
  id: z43.string(),
  emailNormalized: z43.string(),
  firstSeenAt: z43.string(),
  lastSeenAt: z43.string(),
  attributes: z43.record(z43.any()).optional()
});

// src/domain/consent-grant/consent-grant.schema.ts
import { z as z44 } from "zod";
var consentScopeSchema = z44.object({
  ttMarketing: z44.boolean().optional(),
  partnerMarketing: z44.boolean().optional(),
  namedThirdParty: z44.array(z44.string()).optional()
});
var consentGrantSchema = z44.object({
  id: z44.bigint(),
  subscriber: z44.bigint(),
  campaign: z44.bigint(),
  basis: consentBasisGrantEnum,
  scope: consentScopeSchema,
  disclosureText: z44.string().optional(),
  disclosureVersion: z44.string().optional(),
  sourceCreative: z44.string().optional(),
  sourcePartner: z44.string().optional(),
  createdAt: z44.date()
});

// src/domain/consent-grant/consent-grant.response.schema.ts
import { z as z45 } from "zod";
var consentGrantResponseSchema = z45.object({
  id: z45.string(),
  subscriber: z45.string(),
  campaign: z45.string(),
  basis: consentBasisGrantEnum,
  scope: consentScopeSchema,
  disclosureText: z45.string().optional(),
  disclosureVersion: z45.string().optional(),
  sourceCreative: z45.string().optional(),
  sourcePartner: z45.string().optional(),
  createdAt: z45.string()
});

// src/domain/journey-execution/journey-execution.schema.ts
import { z as z46 } from "zod";
var journeyStepSchema = z46.object({
  step: z46.string(),
  input: z46.record(z46.any()).optional(),
  result: z46.string().optional(),
  transition: z46.string().optional(),
  error: z46.string().optional(),
  timestamp: z46.string()
});
var journeyExecutionSchema = z46.object({
  id: z46.bigint(),
  lead: z46.bigint(),
  startedAt: z46.date(),
  completedAt: z46.date().optional(),
  status: journeyExecutionStatusEnum.optional(),
  steps: z46.array(journeyStepSchema),
  createdAt: z46.date()
});

// src/domain/journey-execution/journey-execution.response.schema.ts
import { z as z47 } from "zod";
var journeyExecutionResponseSchema = z47.object({
  id: z47.string(),
  lead: z47.string(),
  startedAt: z47.string(),
  completedAt: z47.string().optional(),
  status: journeyExecutionStatusEnum.optional(),
  steps: z47.array(journeyStepSchema),
  createdAt: z47.string()
});

// src/domain/event-log/event-log.schema.ts
import { z as z48 } from "zod";
var eventLogSchema = z48.object({
  id: z48.bigint(),
  kind: z48.string(),
  lead: z48.bigint().optional(),
  subscriber: z48.bigint().optional(),
  campaign: z48.bigint().optional(),
  payload: z48.record(z48.any()).optional(),
  createdAt: z48.date()
});

// src/domain/event-log/event-log.response.schema.ts
import { z as z49 } from "zod";
var eventLogResponseSchema = z49.object({
  id: z49.string(),
  kind: z49.string(),
  lead: z49.string().optional(),
  subscriber: z49.string().optional(),
  campaign: z49.string().optional(),
  payload: z49.record(z49.any()).optional(),
  createdAt: z49.string()
});

// src/domain/request-log/request-log.schema.ts
import { z as z50 } from "zod";
var requestLogSchema = z50.object({
  id: z50.bigint(),
  visitorCookie: z50.string().optional(),
  ipAddress: z50.string().optional(),
  userAgent: z50.string().optional(),
  path: z50.string().optional(),
  method: z50.string().optional(),
  campaign: z50.bigint().optional(),
  createdAt: z50.date()
});

// src/domain/request-log/request-log.response.schema.ts
import { z as z51 } from "zod";
var requestLogResponseSchema = z51.object({
  id: z51.string(),
  visitorCookie: z51.string().optional(),
  ipAddress: z51.string().optional(),
  userAgent: z51.string().optional(),
  path: z51.string().optional(),
  method: z51.string().optional(),
  campaign: z51.string().optional(),
  createdAt: z51.string()
});

// src/domain/offers/walk-start-url.schema.ts
import { z as z52 } from "zod";
var walkStartUrlSchema = z52.string().url();

// src/domain/offers/accept-skip.request.schema.ts
import { z as z53 } from "zod";
var acceptSkipRequestSchema = z53.object({
  placementId: z53.string()
  // bigint PK as string (JSON-safe)
});

// src/domain/offers/accept-skip.response.schema.ts
import { z as z54 } from "zod";
var acceptSkipResponseSchema = z54.object({
  nextStepUrl: z54.string(),
  // URL of the next spot or thank-you page
  targetUrl: z54.string().nullable(),
  // Offer target URL (null for skip)
  step: walkStepResponseSchema
  // Fully rendered next step (offer or thank-you)
});

// src/domain/promo-type/promo-type.schema.ts
import { z as z55 } from "zod";
var promoTypeSchema = z55.object({
  id: z55.bigint(),
  slug: z55.string().min(1).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Slug must be lowercase words joined by dashes"),
  name: z55.string().min(1),
  status: promoTypeStatusEnum,
  createdAt: z55.date(),
  updatedAt: z55.date()
});

// src/domain/promo-type/promo-type.response.schema.ts
import { z as z56 } from "zod";
var promoTypeResponseSchema = promoTypeSchema.extend({
  id: z56.string(),
  createdAt: z56.string(),
  updatedAt: z56.string()
});

// src/domain/promo-type/create.request.schema.ts
var createPromoTypeSchema = promoTypeSchema.partial({
  id: true,
  createdAt: true,
  updatedAt: true,
  status: true
  // defaults to 'active' in the DB; the service sets it
});

// src/domain/promo-type/update.request.schema.ts
var updatePromoTypeSchema = createPromoTypeSchema.partial();

// src/domain/capture-rejection/capture-rejection.schema.ts
import { z as z57 } from "zod";
var captureRejectionSchema = z57.object({
  id: z57.bigint(),
  campaignId: z57.bigint().optional(),
  campaignLinkId: z57.bigint().optional(),
  siteId: z57.string().optional(),
  emailOriginal: z57.string(),
  emailHash: z57.string(),
  ipAddress: z57.string().optional(),
  country: z57.string().optional(),
  reason: z57.string(),
  failedSteps: z57.array(failedStepSchema),
  sourceParams: sourceParamsSchema.optional(),
  isTest: z57.boolean(),
  createdAt: z57.date()
});

// src/domain/capture-rejection/capture-rejection.response.schema.ts
import { z as z58 } from "zod";
var captureRejectionResponseSchema = captureRejectionSchema.extend({
  id: z58.string(),
  campaignId: z58.string().optional(),
  campaignLinkId: z58.string().optional(),
  createdAt: z58.string()
});

// src/domain/email-allow-list/email-allow-list.schema.ts
import { z as z59 } from "zod";
var emailAllowListSchema = z59.object({
  id: z59.bigint(),
  kind: allowListKindEnum,
  value: z59.string().min(1),
  note: z59.string().optional(),
  createdAt: z59.date()
});

// src/domain/email-allow-list/email-allow-list.response.schema.ts
import { z as z60 } from "zod";
var emailAllowListResponseSchema = emailAllowListSchema.extend({
  id: z60.string(),
  createdAt: z60.string()
});

// src/domain/email-allow-list/create.request.schema.ts
import { z as z61 } from "zod";
var createEmailAllowListSchema = emailAllowListSchema.partial({
  id: true,
  createdAt: true,
  value: true
}).extend({
  value: z61.string().trim().toLowerCase().min(3).max(254).refine((value) => EMAIL_SHAPE.test(value) || DOMAIN_SHAPE.test(value), {
    message: "Enter an email address (a@b.com) or a domain (example.com)"
  }),
  note: z61.string().trim().max(500).optional()
}).superRefine((entry, ctx) => {
  const looksLikeEmail = entry.value.includes("@");
  if (entry.kind === "email" && !looksLikeEmail) {
    ctx.addIssue({
      code: z61.ZodIssueCode.custom,
      path: ["value"],
      message: 'kind "email" needs an address, not a bare domain'
    });
  }
  if (entry.kind === "domain" && looksLikeEmail) {
    ctx.addIssue({
      code: z61.ZodIssueCode.custom,
      path: ["value"],
      message: 'kind "domain" needs a bare domain, not an address'
    });
  }
});
var EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
var DOMAIN_SHAPE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

// src/domain/cto-setting/cto-approved-domains.schema.ts
import { z as z62 } from "zod";
var ctoApprovedDomainsSchema = z62.object({
  domains: z62.string().max(1e4)
});

// src/domain/cto-setting/cto-approved-domains.update.request.schema.ts
var ctoApprovedDomainsUpdateSchema = ctoApprovedDomainsSchema;

// src/domain/cto-setting/parse-approved-domains.ts
function parseApprovedDomains(raw) {
  const seen = /* @__PURE__ */ new Set();
  const domains = [];
  for (const candidate of raw.split(",")) {
    const domain = candidate.trim().toLowerCase();
    if (domain === "" || seen.has(domain)) {
      continue;
    }
    seen.add(domain);
    domains.push(domain);
  }
  return domains;
}

// src/domain/partner-credential/partner-credential.schema.ts
import { z as z63 } from "zod";
var partnerCredentialSchema = z63.object({
  id: z63.bigint(),
  advertiserId: z63.bigint(),
  label: z63.string(),
  kind: partnerCredentialKindEnum,
  keyId: z63.string(),
  hint: z63.string().optional(),
  createdBy: z63.string(),
  createdAt: z63.date(),
  rotatedAt: z63.date().optional(),
  revokedAt: z63.date().optional()
});

// src/domain/partner-credential/partner-credential.response.schema.ts
import { z as z64 } from "zod";
var partnerCredentialResponseSchema = partnerCredentialSchema.omit({
  keyId: true,
  createdBy: true
}).extend({
  id: z64.string(),
  advertiserId: z64.string(),
  hint: z64.string().nullable(),
  createdAt: z64.string(),
  rotatedAt: z64.string().nullable(),
  revokedAt: z64.string().nullable()
});

// src/domain/partner-credential/create.request.schema.ts
import { z as z65 } from "zod";
var createPartnerCredentialSchema = z65.object({
  advertiserId: zCoerceBigInt(),
  label: z65.string().min(1),
  kind: partnerCredentialKindEnum,
  secret: z65.string().min(1)
});
var rotatePartnerCredentialSchema = z65.object({
  secret: z65.string().min(1)
});

// src/domain/campaign-route-rule/campaign-route-rule.schema.ts
import { z as z66 } from "zod";
var campaignRouteRuleSchema = z66.object({
  id: z66.bigint(),
  campaignId: z66.bigint(),
  matchStatus: z66.number().int().optional(),
  bodyContains: z66.string().optional(),
  bodyNotContains: z66.string().optional(),
  destinationCampaignId: z66.bigint(),
  enabled: z66.boolean(),
  createdAt: z66.date(),
  updatedAt: z66.date()
});

// src/domain/campaign-route-rule/campaign-route-rule.response.schema.ts
import { z as z67 } from "zod";
var campaignRouteRuleResponseSchema = campaignRouteRuleSchema.extend({
  id: z67.string(),
  campaignId: z67.string(),
  matchStatus: z67.number().int().nullable().optional(),
  bodyContains: z67.string().nullable().optional(),
  bodyNotContains: z67.string().nullable().optional(),
  destinationCampaignId: z67.string(),
  createdAt: z67.string(),
  updatedAt: z67.string()
});

// src/domain/campaign-route-rule/create.request.schema.ts
import { z as z68 } from "zod";
var createCampaignRouteRuleSchema = z68.object({
  campaignId: zCoerceBigIntOptional(),
  matchStatus: z68.number().int().optional(),
  bodyContains: z68.string().optional(),
  bodyNotContains: z68.string().optional(),
  destinationCampaignId: zCoerceBigInt(),
  enabled: z68.boolean().default(true)
});
var updateCampaignRouteRuleSchema = createCampaignRouteRuleSchema.partial();

// src/domain/distribution-attempt/distribution-attempt.schema.ts
import { z as z69 } from "zod";
var requestSummarySchema = z69.object({
  method: z69.string().optional(),
  url: z69.string().optional(),
  headerNames: z69.array(z69.string()).optional()
}).passthrough();
var distributionAttemptSchema = z69.object({
  id: z69.bigint(),
  leadId: z69.bigint(),
  campaignId: z69.bigint(),
  advertiserId: z69.bigint(),
  kind: attemptKindEnum,
  attemptNo: z69.number().int(),
  outcome: attemptOutcomeEnum,
  reason: z69.string().optional(),
  httpStatus: z69.number().int().optional(),
  requestSummary: requestSummarySchema,
  responseExcerpt: z69.string().optional(),
  attributedCampaignId: z69.bigint().optional(),
  routedFromAttemptId: z69.bigint().optional(),
  testRunId: z69.bigint().optional(),
  jobId: z69.string().optional(),
  isTest: z69.boolean(),
  startedAt: z69.date(),
  finishedAt: z69.date().optional()
});

// src/domain/distribution-attempt/distribution-attempt.response.schema.ts
import { z as z70 } from "zod";
var distributionAttemptResponseSchema = distributionAttemptSchema.extend({
  id: z70.string(),
  leadId: z70.string(),
  campaignId: z70.string(),
  advertiserId: z70.string(),
  reason: z70.string().nullable().optional(),
  httpStatus: z70.number().int().nullable().optional(),
  responseExcerpt: z70.string().nullable().optional(),
  attributedCampaignId: z70.string().nullable().optional(),
  routedFromAttemptId: z70.string().nullable().optional(),
  testRunId: z70.string().nullable().optional(),
  jobId: z70.string().nullable().optional(),
  startedAt: z70.string(),
  finishedAt: z70.string().nullable().optional()
});

// src/domain/partner-response-log/partner-response-log.schema.ts
import { z as z71 } from "zod";
var partnerResponseLogSchema = z71.object({
  id: z71.bigint(),
  attemptId: z71.bigint(),
  headers: z71.record(z71.any()),
  body: z71.string(),
  createdAt: z71.date()
});

// src/domain/esp-action/esp-action.schema.ts
import { z as z72 } from "zod";
var espActionSchema = z72.object({
  id: z72.bigint(),
  leadId: z72.bigint(),
  espDestinationId: z72.bigint().optional(),
  provider: espProviderEnum,
  brandList: brandListEnum.optional(),
  action: espActionKindEnum,
  outcome: espActionOutcomeEnum,
  reason: z72.string().optional(),
  responseExcerpt: z72.string().optional(),
  httpStatus: z72.number().int().optional(),
  attemptNo: z72.number().int(),
  isTest: z72.boolean(),
  createdAt: z72.date()
});

// src/domain/esp-action/esp-action.response.schema.ts
import { z as z73 } from "zod";
var espActionResponseSchema = espActionSchema.extend({
  id: z73.string(),
  leadId: z73.string(),
  espDestinationId: z73.string().nullable().optional(),
  brandList: brandListEnum.nullable().optional(),
  reason: z73.string().nullable().optional(),
  responseExcerpt: z73.string().nullable().optional(),
  httpStatus: z73.number().int().nullable().optional(),
  createdAt: z73.string()
});

// src/domain/campaign-test-run/campaign-test-run.schema.ts
import { z as z74 } from "zod";
var campaignTestRunSchema = z74.object({
  id: z74.bigint(),
  campaignId: z74.bigint(),
  campaignLinkId: z74.bigint().optional(),
  leadId: z74.bigint().optional(),
  mode: testRunModeEnum,
  testEmail: z74.string(),
  requestedBy: z74.string(),
  status: testRunStatusEnum,
  summary: z74.record(z74.any()).optional(),
  createdAt: z74.date(),
  finishedAt: z74.date().optional()
});

// src/domain/campaign-test-run/campaign-test-run.response.schema.ts
import { z as z75 } from "zod";
var campaignTestRunResponseSchema = campaignTestRunSchema.extend({
  id: z75.string(),
  campaignId: z75.string(),
  campaignLinkId: z75.string().nullable().optional(),
  leadId: z75.string().nullable().optional(),
  summary: z75.record(z75.any()).nullable().optional(),
  createdAt: z75.string(),
  finishedAt: z75.string().nullable().optional()
});

// src/domain/campaign-test-run/create.request.schema.ts
import { z as z76 } from "zod";
var createCampaignTestRunSchema = z76.object({
  mode: testRunModeEnum,
  testEmail: z76.string().email(),
  campaignLinkId: zCoerceBigIntOptional()
});

// src/domain/worker-heartbeat/worker-heartbeat.schema.ts
import { z as z77 } from "zod";
var workerHeartbeatSchema = z77.object({
  id: z77.bigint(),
  instanceId: z77.string(),
  startedAt: z77.date(),
  lastBeatAt: z77.date(),
  version: z77.string(),
  stats: z77.record(z77.any())
});

// src/domain/worker-heartbeat/worker-heartbeat.response.schema.ts
import { z as z78 } from "zod";
var workerHeartbeatResponseSchema = workerHeartbeatSchema.extend({
  id: z78.string(),
  startedAt: z78.string(),
  lastBeatAt: z78.string()
});

// src/domain/partner-adapters/credential-key-status.response.schema.ts
import { z as z79 } from "zod";
var credentialKeyStatusResponseSchema = z79.object({
  source: z79.enum(["env", "dev_default", "none"]),
  keyId: z79.string().nullable(),
  previousKeyId: z79.string().nullable(),
  rowsOnCurrentKey: z79.number().int(),
  rowsOnPreviousKey: z79.number().int(),
  rowsOnUnknownKey: z79.number().int()
});

// src/domain/partner-adapters/credential-reencrypt.response.schema.ts
import { z as z80 } from "zod";
var reencryptResponseSchema = z80.object({
  reencrypted: z80.number().int(),
  skipped: z80.number().int(),
  unknownKey: z80.number().int()
});

// src/domain/partner-adapters/partner-credential-row.response.schema.ts
import { z as z81 } from "zod";
var partnerCredentialRowResponseSchema = partnerCredentialResponseSchema.extend({
  advertiserName: z81.string()
});

// src/domain/partner-adapters/route-rule-upsert.request.schema.ts
import { z as z82 } from "zod";
var upsertRouteRuleSchema = createCampaignRouteRuleSchema.pick({ destinationCampaignId: true, enabled: true }).extend({
  matchStatus: z82.number().int().min(100).max(599).nullable().optional(),
  bodyContains: z82.string().trim().min(1).max(200).nullable().optional(),
  bodyNotContains: z82.string().trim().min(1).max(200).nullable().optional()
}).superRefine((rule, ctx) => {
  const hasCondition = rule.matchStatus !== null && rule.matchStatus !== void 0 || rule.bodyContains !== null && rule.bodyContains !== void 0 || rule.bodyNotContains !== null && rule.bodyNotContains !== void 0;
  if (!hasCondition) {
    ctx.addIssue({
      code: z82.ZodIssueCode.custom,
      path: [],
      message: "At least one condition is required"
    });
  }
});

// src/domain/partner-adapters/route-rule-row.response.schema.ts
import { z as z83 } from "zod";
var routeRuleRowResponseSchema = campaignRouteRuleResponseSchema.extend({
  campaignName: z83.string(),
  destinationCampaignName: z83.string()
});

// src/domain/distribution/attempt.response.schema.ts
import { z as z84 } from "zod";
var distributionAttemptRowResponseSchema = z84.object({
  id: z84.string(),
  leadId: z84.string(),
  leadEmail: z84.string(),
  campaignId: z84.string(),
  campaignName: z84.string(),
  advertiserId: z84.string(),
  advertiserName: z84.string(),
  kind: attemptKindEnum,
  attemptNo: z84.number().int(),
  outcome: attemptOutcomeEnum,
  reason: z84.string().nullable(),
  httpStatus: z84.number().int().nullable(),
  requestSummary: requestSummarySchema,
  responseExcerpt: z84.string().nullable(),
  attributedCampaignId: z84.string().nullable(),
  attributedCampaignName: z84.string().nullable(),
  routedFromAttemptId: z84.string().nullable(),
  testRunId: z84.string().nullable(),
  jobId: z84.string().nullable(),
  isTest: z84.boolean(),
  startedAt: z84.string(),
  finishedAt: z84.string().nullable()
});
var distributionAttemptDetailResponseSchema = distributionAttemptRowResponseSchema.extend({
  responseLog: z84.object({
    headers: z84.record(z84.string()),
    body: z84.string(),
    createdAt: z84.string()
  }).nullable()
});

// src/domain/distribution/dlq.response.schema.ts
import { z as z85 } from "zod";
var dlqJobResponseSchema = z85.object({
  id: z85.string(),
  taskIdentifier: z85.string(),
  payload: z85.record(z85.unknown()),
  leadId: z85.string().nullable(),
  campaignId: z85.string().nullable(),
  attempts: z85.number().int(),
  maxAttempts: z85.number().int(),
  lastError: z85.string().nullable(),
  runAt: z85.string(),
  createdAt: z85.string(),
  updatedAt: z85.string(),
  key: z85.string().nullable()
});

// src/domain/partner-endpoint/partner-endpoint.schema.ts
import { z as z86 } from "zod";
function isNonBlank(value) {
  return value !== void 0 && value.trim().length > 0;
}
var endpointBodyFieldSchema = z86.object({
  name: z86.string(),
  value: z86.string()
});
var endpointTransportSchema = z86.object({
  method: crmMethodEnum.optional(),
  get_url: z86.string().optional(),
  post_url: z86.string().optional(),
  adapter: z86.string().optional(),
  placeholder_syntax: placeholderSyntaxEnum.optional(),
  headers: z86.record(z86.string()).optional(),
  body_template: z86.string().optional(),
  body_fields: z86.array(endpointBodyFieldSchema).optional(),
  response_rules: crmResponseRulesSchema.optional()
}).refine(
  (transport) => isNonBlank(transport.get_url) || isNonBlank(transport.post_url),
  // A URL-less endpoint is not representable (ADR-016 §1).
  { message: "An endpoint requires at least one of get_url / post_url" }
).refine(
  (transport) => !(transport.body_template !== void 0 && transport.body_fields !== void 0),
  // The template textarea and the structured fields are the two body forms
  // (ADR-016 §5) — a Zod refinement, not a runtime check (epic README #5).
  { message: "body_template and body_fields are mutually exclusive" }
);
var endpointVariableSchema = z86.object({
  key: z86.string().regex(/^[a-z][a-z0-9_]*$/, {
    message: "Variable keys are lowercase snake_case identifiers"
  }),
  label: z86.string().min(1),
  required: z86.boolean(),
  default: z86.string().optional(),
  hint: z86.string().optional()
});
var endpointVariablesSchema = z86.array(endpointVariableSchema).refine(
  (variables) => new Set(variables.map((variable) => variable.key)).size === variables.length,
  { message: "Variable keys must be unique" }
);
var partnerEndpointSchema = z86.object({
  id: z86.bigint(),
  advertiserId: z86.bigint(),
  name: z86.string(),
  status: partnerEndpointStatusEnum,
  transport: endpointTransportSchema,
  credentialId: z86.bigint().optional(),
  variables: endpointVariablesSchema,
  createdAt: z86.date(),
  updatedAt: z86.date()
});

// src/domain/partner-endpoint/create.request.schema.ts
import { z as z87 } from "zod";
var createPartnerEndpointSchema = z87.object({
  advertiserId: zCoerceBigInt(),
  name: z87.string().min(1),
  transport: endpointTransportSchema,
  credentialId: zCoerceBigIntOptional().optional(),
  variables: endpointVariablesSchema
});

// src/domain/partner-endpoint/update.request.schema.ts
import { z as z88 } from "zod";
var BIGINT_INVALID2 = "__leads_zod_bigint_invalid__";
var zClearableBigIntOptional = () => z88.union([z88.literal(""), z88.null(), z88.string(), z88.number(), z88.bigint()]).transform((v) => {
  if (v === "" || v === null) return null;
  if (typeof v === "bigint") return v;
  if (typeof v === "string" && v.trim().length === 0) return BIGINT_INVALID2;
  try {
    return BigInt(v);
  } catch {
    return BIGINT_INVALID2;
  }
}).pipe(z88.bigint().nullable());
var updatePartnerEndpointSchema = createPartnerEndpointSchema.omit({ advertiserId: true, credentialId: true }).partial().extend({ credentialId: zClearableBigIntOptional().optional() });

// src/domain/partner-endpoint/partner-endpoint.response.schema.ts
import { z as z89 } from "zod";
var partnerEndpointResponseSchema = partnerEndpointSchema.extend({
  id: z89.string(),
  advertiserId: z89.string(),
  credentialId: z89.string().nullable(),
  credentialLabel: z89.string().nullable(),
  credentialHint: z89.string().nullable(),
  createdAt: z89.string(),
  updatedAt: z89.string()
});

// src/domain/partner-endpoint/values.ts
var INLINE_TRANSPORT_KEYS = [
  "get_url",
  "post_url",
  "direct_url",
  "method",
  "headers",
  "body_template",
  "body_fields",
  "auth",
  "response_rules",
  "adapter",
  "getCrmUrl",
  "postCrmUrl",
  "directCrmUrl",
  "sendMethod"
];
function stripInlineTransportKeys(crmConfig) {
  const stripped = { ...crmConfig };
  for (const key of INLINE_TRANSPORT_KEYS) {
    delete stripped[key];
  }
  return stripped;
}
function validateEndpointValues(variables, values, forbiddenSpellings) {
  const issues = [];
  for (const variable of variables) {
    const value = values[variable.key];
    if (value === void 0) {
      if (variable.required && variable.default === void 0) {
        issues.push(
          `Missing required value "${variable.key}"` + (variable.label ? ` (${variable.label})` : "") + "."
        );
      }
      continue;
    }
    for (const spelling of forbiddenSpellings) {
      if (value.includes(spelling)) {
        issues.push(
          `Value for "${variable.key}" contains the placeholder spelling ${JSON.stringify(
            spelling
          )} \u2014 values are literals (ADR-016 \xA73).`
        );
      }
    }
    if (value.includes("{{")) {
      issues.push(`Value for "${variable.key}" contains "{{" \u2014 values are literals (ADR-016 \xA73).`);
    }
  }
  return issues.length === 0 ? { ok: true } : { ok: false, issues };
}

// src/domain/pageview/pageview.request.schema.ts
import { z as z90 } from "zod";
var pageviewRequestSchema = z90.object({
  path: z90.string().trim().min(1).max(2048),
  campaignRef: z90.string().trim().min(1).max(512).optional(),
  utm_siteid: provenanceStringSchema.optional(),
  sub_id: provenanceStringSchema.optional(),
  // Accepted for contract completeness with the URL that produced the view;
  // request_log has no utm_source column, so it is not persisted (yet).
  utm_source: provenanceStringSchema.optional()
});

// src/domain/pageview/pageview.response.schema.ts
import { z as z91 } from "zod";
var pageviewResponseSchema = z91.object({
  recorded: z91.boolean()
});

// src/domain/partner-config-check/partner-config-issue.schema.ts
import { z as z92 } from "zod";
var partnerConfigIssueCodeEnum = z92.enum([
  "preping_equals_send",
  "preping_same_address",
  "simple_url_with_saved_endpoint",
  "no_accepted_phrase",
  "duplicates_billable",
  "endpoint_no_accepted_phrase"
]);
var partnerConfigSeverityEnum = z92.enum(["block", "warn", "info"]);
var partnerConfigIssueSchema = z92.object({
  code: partnerConfigIssueCodeEnum,
  severity: partnerConfigSeverityEnum,
  field: z92.string(),
  message: z92.string(),
  existing: z92.boolean().optional()
});
var partnerConfigCheckResponseSchema = z92.object({
  issues: z92.array(partnerConfigIssueSchema)
});

// src/domain/partner-config-check/config-check.request.schema.ts
import { z as z93 } from "zod";
var campaignConfigCheckRequestSchema = z93.object({
  campaignId: zCoerceBigIntOptional().optional(),
  advertiserId: zCoerceBigIntOptional().optional(),
  campaignStyle: campaignStyleEnum.optional(),
  crmConfig: z93.record(z93.unknown()).optional(),
  suppression: z93.object({
    enabled: z93.boolean().optional(),
    prepingApiUrl: z93.string().nullable().optional()
  }).strict().optional()
}).strict();
var endpointConfigCheckRequestSchema = z93.object({
  endpointId: zCoerceBigIntOptional().optional(),
  transport: z93.record(z93.unknown()),
  variables: z93.array(z93.record(z93.unknown())).optional()
}).strict();

// src/domain/campaign-test-run/test-run.admin.schema.ts
import { z as z94 } from "zod";
var testRunOutcomeSchema = z94.enum(["passed", "failed"]);
var createTestRunRequestSchema = createCampaignTestRunSchema.extend({
  testEmail: z94.string().email().optional(),
  // The base field rejects an absent key (its union has no `undefined`
  // member — only `''`/`null` map to omitted); the harness contract makes
  // the key itself optional, present values still coerce to bigint.
  campaignLinkId: zCoerceBigIntOptional().optional(),
  siteId: z94.string().max(64).optional(),
  subId: z94.string().max(64).optional(),
  requestedBy: z94.string().max(80).optional()
}).superRefine((value, ctx) => {
  if (value.mode === "page") {
    if (value.testEmail === void 0) {
      ctx.addIssue({
        code: z94.ZodIssueCode.custom,
        path: ["testEmail"],
        message: "page mode requires testEmail"
      });
    }
    if (value.campaignLinkId !== void 0) {
      ctx.addIssue({
        code: z94.ZodIssueCode.custom,
        path: ["campaignLinkId"],
        message: "page mode forbids campaignLinkId"
      });
    }
    return;
  }
  if (value.mode === "cto_link") {
    if (value.campaignLinkId === void 0) {
      ctx.addIssue({
        code: z94.ZodIssueCode.custom,
        path: ["campaignLinkId"],
        message: "cto_link mode requires campaignLinkId"
      });
    }
    if (value.testEmail === void 0) {
      ctx.addIssue({
        code: z94.ZodIssueCode.custom,
        path: ["testEmail"],
        message: "cto_link mode requires testEmail"
      });
    }
    return;
  }
  if (value.campaignLinkId !== void 0) {
    ctx.addIssue({
      code: z94.ZodIssueCode.custom,
      path: ["campaignLinkId"],
      message: "admin mode forbids campaignLinkId"
    });
  }
});
var testRunSummarySchema = z94.object({
  id: z94.string(),
  campaignId: z94.string(),
  campaignName: z94.string(),
  campaignStyle: campaignStyleEnum,
  mode: testRunModeEnum,
  testEmail: z94.string(),
  requestedBy: z94.string(),
  status: testRunStatusEnum,
  outcome: testRunOutcomeSchema.nullable(),
  createdAt: z94.string(),
  finishedAt: z94.string().nullable(),
  campaignLinkId: z94.string().nullable(),
  launchUrl: z94.string().nullable(),
  warnings: z94.array(z94.string()).default([])
});
var testRunDetailLeadSchema = z94.object({
  id: z94.string(),
  emailOriginal: z94.string(),
  isTest: z94.boolean(),
  siteId: z94.string().nullable(),
  subId: z94.string().nullable(),
  beehiivLookupStatus: beehiivLookupStatusEnum.nullable(),
  country: z94.string().nullable(),
  createdAt: z94.string(),
  processingState: processingStateSchema.nullable()
});
var testRunAttemptRequestSummarySchema = z94.object({
  method: z94.string().optional(),
  url: z94.string().optional(),
  headerNames: z94.array(z94.string()).optional()
});
var testRunAttemptSchema = z94.object({
  id: z94.string(),
  kind: attemptKindEnum,
  attemptNo: z94.number().int(),
  outcome: attemptOutcomeEnum,
  reason: z94.string().nullable(),
  httpStatus: z94.number().int().nullable(),
  requestSummary: testRunAttemptRequestSummarySchema,
  responseExcerpt: z94.string().nullable(),
  receivingCampaignId: z94.string().nullable(),
  receivingCampaignName: z94.string().nullable(),
  advertiserName: z94.string().nullable(),
  attributedCampaignId: z94.string().nullable(),
  routedFromAttemptId: z94.string().nullable(),
  isTest: z94.boolean(),
  startedAt: z94.string(),
  finishedAt: z94.string().nullable()
});
var testRunDetailSchema = z94.object({
  run: testRunSummarySchema.extend({
    summary: z94.record(z94.unknown()).nullable()
  }),
  lead: testRunDetailLeadSchema.nullable(),
  attempts: z94.array(testRunAttemptSchema)
});
var testRunListSchema = z94.object({
  rows: z94.array(testRunSummarySchema),
  total: z94.number().int()
});
var activationCheckSchema = z94.object({
  campaignId: z94.string(),
  gate: z94.literal("warning"),
  passed: z94.boolean(),
  lastPassedRun: z94.object({
    id: z94.string(),
    finishedAt: z94.string()
  }).nullable(),
  lastRun: z94.object({
    id: z94.string(),
    status: testRunStatusEnum,
    outcome: testRunOutcomeSchema.nullable(),
    finishedAt: z94.string().nullable()
  }).nullable()
});

// src/domain/campaign-test-run/test-page.response.schema.ts
import { z as z95 } from "zod";
var testPageResponseSchema = z95.object({
  run: z95.object({
    id: z95.string(),
    state: z95.enum(["open", "used", "expired"]),
    status: testRunStatusEnum,
    testEmail: z95.string(),
    campaignName: z95.string(),
    sendMode: z95.enum(["lead_post", "click_out"]),
    expiresAt: z95.string()
  }),
  step: walkStepResponseSchema
});
export {
  CRM_CONFIG_ALIASES,
  CTO_DEFAULT_MERGE_TAG,
  CTO_MERGE_TAG_MAX_LENGTH,
  CUSTOM_PARAMS_MAX_KEYS,
  CUSTOM_PARAM_KEY_PATTERN,
  CUSTOM_PARAM_MAX_VALUE_LENGTH,
  FACEBOOK_SITE_ID,
  INLINE_TRANSPORT_KEYS,
  OPT_IN_DISCLAIMER_DEFAULT,
  PLACEMENT_SPOT_MAX,
  SOURCE_SLUG_FALLBACK,
  SOURCE_SLUG_MAX_LENGTH,
  SOURCE_SLUG_PATTERN,
  acceptSkipRequestSchema,
  acceptSkipResponseSchema,
  acquisitionEventResponseSchema,
  acquisitionEventSchema,
  activationCheckSchema,
  advertiserResponseSchema,
  advertiserSchema,
  advertiserStatusEnum,
  allowListKindEnum,
  apiResponseSchema,
  attemptKindEnum,
  attemptOutcomeEnum,
  beehiivLookupStatusEnum,
  brandListEnum,
  buildSourceSlug,
  campaignAggregateCreateSchema,
  campaignAggregateUpdateSchema,
  campaignAssetUploadResponseSchema,
  campaignCmConfigSchema,
  campaignConfigCheckRequestSchema,
  campaignEligibilityStepSchema,
  campaignLinkCustomParamSchema,
  campaignLinkCustomParamsSchema,
  campaignLinkResponseSchema,
  campaignLinkSchema,
  campaignLinkStatusEnum,
  campaignLinkValidationsInputSchema,
  campaignLinkValidationsSchema,
  campaignListResponseSchema,
  campaignPlacementSchema,
  campaignResponseSchema,
  campaignRouteRuleResponseSchema,
  campaignRouteRuleSchema,
  campaignScheduleRequestSchema,
  campaignSchema,
  campaignSiteRestrictionElementSchema,
  campaignSiteRestrictionResponseSchema,
  campaignSiteRestrictionSchema,
  campaignStatusEnum,
  campaignStatusReasonEnum,
  campaignStyleEnum,
  campaignSubTypeEnum,
  campaignSuppressionSchema,
  campaignTestRunResponseSchema,
  campaignTestRunSchema,
  captureRejectionResponseSchema,
  captureRejectionSchema,
  captureRequestSchema,
  captureResponseSchema,
  captureStrategyEnum,
  captureStrategyEventEnum,
  consentBasisEnum,
  consentBasisGrantEnum,
  consentGrantResponseSchema,
  consentGrantSchema,
  consentScopeSchema,
  createAdvertiserSchema,
  createCampaignCmConfigSchema,
  createCampaignEligibilityStepSchema,
  createCampaignLinkSchema,
  createCampaignPlacementSchema,
  createCampaignRouteRuleSchema,
  createCampaignSchema,
  createCampaignSuppressionSchema,
  createCampaignTestRunSchema,
  createEmailAllowListSchema,
  createEspDestinationSchema,
  createPartnerCredentialSchema,
  createPartnerEndpointSchema,
  createPromoTypeSchema,
  createPublisherSchema,
  createSiteSchema,
  createSuppressionFileSchema,
  createTestRunRequestSchema,
  credentialKeyStatusResponseSchema,
  crmAuthSchema,
  crmConfigSchema,
  crmMethodEnum,
  crmPrepingRuleSchema,
  crmResponseRulesSchema,
  crmSendModeEnum,
  ctoApprovedDomainsSchema,
  ctoApprovedDomainsUpdateSchema,
  ctoMergeTagForProvider,
  ctoMergeTagPresets,
  ctoResolveQueryKeys,
  ctoResolveResponseSchema,
  customParamsSchema,
  distributionAttemptDetailResponseSchema,
  distributionAttemptResponseSchema,
  distributionAttemptRowResponseSchema,
  distributionAttemptSchema,
  dlqJobResponseSchema,
  eligibilityStepKindEnum,
  emailAllowListResponseSchema,
  emailAllowListSchema,
  endpointBodyFieldSchema,
  endpointConfigCheckRequestSchema,
  endpointTransportSchema,
  endpointVariableSchema,
  endpointVariablesSchema,
  espActionKindEnum,
  espActionOutcomeEnum,
  espActionResponseSchema,
  espActionSchema,
  espDestinationSchema,
  espProviderEnum,
  eventLogResponseSchema,
  eventLogSchema,
  extResolveQueryKeys,
  extResolveResponseSchema,
  facebookShareLimit,
  failedStepSchema,
  flowKindEnum,
  hashAlgorithmEnum,
  isWithinRunWindow,
  journeyExecutionResponseSchema,
  journeyExecutionSchema,
  journeyExecutionStatusEnum,
  journeyStepSchema,
  leadApiResponseSchema,
  leadDetailApiResponseSchema,
  leadDetailResponseSchema,
  leadListApiResponseSchema,
  leadListResponseSchema,
  leadListRowResponseSchema,
  leadPostbackResponseSchema,
  leadResponseSchema,
  leadSchema,
  leadShareResponseSchema,
  limitTypeEnum,
  normalizeCrmConfig,
  offerTemplateEnum,
  pageviewRequestSchema,
  pageviewResponseSchema,
  parseApprovedDomains,
  partnerConfigCheckResponseSchema,
  partnerConfigIssueCodeEnum,
  partnerConfigIssueSchema,
  partnerConfigSeverityEnum,
  partnerCredentialKindEnum,
  partnerCredentialResponseSchema,
  partnerCredentialRowResponseSchema,
  partnerCredentialSchema,
  partnerEndpointResponseSchema,
  partnerEndpointSchema,
  partnerEndpointStatusEnum,
  partnerResponseLogSchema,
  placeholderSyntaxEnum,
  processingStageStatusEnum,
  processingStateSchema,
  promoTypeResponseSchema,
  promoTypeSchema,
  promoTypeStatusEnum,
  provenanceStringSchema,
  publicCampaignListResponseSchema,
  publicCampaignResponseSchema,
  publisherResponseSchema,
  publisherSchema,
  publisherStatusEnum,
  reencryptResponseSchema,
  requestLogResponseSchema,
  requestLogSchema,
  requestSummarySchema,
  resolveCapiEnabled,
  rotatePartnerCredentialSchema,
  routeRuleRowResponseSchema,
  siteResponseSchema,
  siteSchema,
  sourceParamsSchema,
  stageStateSchema,
  stripInlineTransportKeys,
  subscriberResponseSchema,
  subscriberSchema,
  suppressionFileAttachedCampaignSchema,
  suppressionFileListRowResponseSchema,
  suppressionFileResponseSchema,
  suppressionFileSchema,
  testPageResponseSchema,
  testRunAttemptRequestSummarySchema,
  testRunAttemptSchema,
  testRunDetailLeadSchema,
  testRunDetailSchema,
  testRunListSchema,
  testRunModeEnum,
  testRunOutcomeSchema,
  testRunStatusEnum,
  testRunSummarySchema,
  transactionalOnEnum,
  updateCampaignLinkSchema,
  updateCampaignRouteRuleSchema,
  updatePartnerEndpointSchema,
  updatePromoTypeSchema,
  upsertRouteRuleSchema,
  validateEndpointValues,
  walkStartUrlSchema,
  walkStepResponseSchema,
  withSourceSlugSuffix,
  workerHeartbeatResponseSchema,
  workerHeartbeatSchema,
  zCoerceBigInt,
  zCoerceBigIntOptional,
  zCoerceDateOptional
};
//# sourceMappingURL=index.js.map